import { useCallback, useEffect, useRef, useState } from 'react';
import { CombinedError, useQuery } from 'urql';

import { GET_ROUTE_PROVIDER_DISPATCH } from 'src/graphql/requests/getRouteProviderDispatch';
import {
  GGetRouteProviderDispatchQuery,
  GProviderDispatchStatus,
} from 'src/graphql/types/generated';
import { useInterval } from 'src/hooks';
import { PRE_DISPATCH_STATUSES } from 'src/utils/thirdPartyDelivery';

export const POLL_INTERVAL_MS = 5000;
// How long the dialog waits on a push or on quotes before saying so. LDS ends
// a stalled row on its own deadline either way.
export const STILL_WAITING_MS = 3 * 60 * 1000;

export type PolledRoute = GGetRouteProviderDispatchQuery['route'];
export type PolledDispatch = NonNullable<PolledRoute['providerDispatch']>;

const WAITING_STATUSES: readonly GProviderDispatchStatus[] = [
  GProviderDispatchStatus.Pushing,
  GProviderDispatchStatus.Quoting,
];

export type ProviderDispatchPoll = {
  // The last route the poll read, kept across errors: urql drops data when a
  // poll fails, which would otherwise turn a quoted route back into the form.
  route: PolledRoute | undefined;
  // Counts good answers, so a caller can tell a fresh one from a re-render.
  revision: number;
  error: CombinedError | undefined;
  stillWaiting: boolean;
  // The route was sent to the provider again from somewhere else.
  superseded: boolean;
  // Call when the dialog's own mark succeeds: its new provider route will
  // carry a new id, which must not read as another session's.
  clearPin: () => void;
  refetch: () => void;
};

// Reads a route's newest provider dispatch, polling while `active`.
//
// There is no dispatch id in GraphQL. Unmark and a failed push keep the
// provider's route id on the old row, so a new mark always brings a new one:
// the id pinned from a pre-dispatch row changing to another id means someone
// else sent the route again.
export const useProviderDispatchPoll = (
  routeId: string,
  { active }: { active: boolean },
): ProviderDispatchPoll => {
  const [{ data, error }, reexecuteQuery] =
    useQuery<GGetRouteProviderDispatchQuery>({
      query: GET_ROUTE_PROVIDER_DISPATCH,
      variables: { input: { id: routeId } },
      pause: !routeId,
      requestPolicy: 'network-only',
    });

  const [route, setRoute] = useState<PolledRoute>();
  const [revision, setRevision] = useState(0);
  const [superseded, setSuperseded] = useState(false);
  const pin = useRef<string | null>(null);
  // Ids of rows the dialog itself replaced: a late answer about the old row
  // must not pin it again.
  const retired = useRef(new Set<string>());

  useEffect(() => {
    if (!data?.route) return;
    setRoute(data.route);
    setRevision((count) => count + 1);

    const record = data.route.providerDispatch;
    const id = record?.externalRouteId;
    if (!record || !id || retired.current.has(id)) return;
    if (pin.current === null) {
      if (PRE_DISPATCH_STATUSES.includes(record.status)) pin.current = id;
    } else if (pin.current !== id) {
      setSuperseded(true);
    }
  }, [data]);

  const clearPin = useCallback((): void => {
    if (pin.current) retired.current.add(pin.current);
    pin.current = null;
  }, []);

  const refetch = useCallback(
    (): void => reexecuteQuery({ requestPolicy: 'network-only' }),
    [reexecuteQuery],
  );

  const status = route?.providerDispatch?.status;
  const waitingOn =
    status && WAITING_STATUSES.includes(status) ? status : undefined;
  const [stillWaiting, setStillWaiting] = useState(false);
  useEffect(() => {
    setStillWaiting(false);
    if (!waitingOn) return undefined;
    const timer = setTimeout(() => setStillWaiting(true), STILL_WAITING_MS);
    return () => clearTimeout(timer);
  }, [waitingOn]);

  useInterval(refetch, active && !superseded ? POLL_INTERVAL_MS : null);

  return {
    route,
    revision,
    error,
    stillWaiting,
    superseded,
    clearPin,
    refetch,
  };
};
